import type { ReactNode } from "react";
import type { AssetType } from "shared/Asset";
import type { ClassificationCategory } from "shared/Classification";
import { isPreview, type PreviewFeature } from "shared/PreviewFeatures";
import Chip from "src/components/common/Chip/Chip";
import { useExtracted } from "#i18n";

export type PreviewLabelProps = {
  /** The feature, asset type, moderation category, or several preview features. */
  feature: PreviewFeature | AssetType | ClassificationCategory | readonly PreviewFeature[];
  /** The feature's name, shown before the chip. Without it only the chip renders. */
  children?: ReactNode;
};

/**
 * The one way the portal, site and docs mark a preview feature. Each feature leaves preview on its own
 * through `shared/PreviewFeatures`, and the chip disappears with it. Where only text fits (a filter
 * chip, an option in a sentence), use `usePreviewText` instead.
 */
const PreviewLabel = ({ feature, children }: PreviewLabelProps) => {
  const translateMessage = useExtracted();
  const chip = isPreview(feature) ? <Chip color="info">{translateMessage("Preview")}</Chip> : null;
  if (children === undefined) return chip;
  if (!chip) return <>{children}</>;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <span>{children}</span>
      {chip}
    </span>
  );
};

export default PreviewLabel;
